import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import {
  ProductCheckoutSchema,
  UpdateProductCheckoutInputSchema,
  UpdateProductInputSchema,
  serializeUpdateProductInput,
} from './product.contract'
import type {
  Product,
  ProductCheckout,
  UpdateProductCheckoutInput,
  UpdateProductInput,
} from './product.contract'
import { parseProductResponse, productKeys } from './product.queries'

function upsertProductInList(products: Product[] | undefined, product: Product) {
  if (!products) return [product]
  const exists = products.some((item) => item.id === product.id)
  if (!exists) return [product, ...products]
  return products.map((item) => (item.id === product.id ? product : item))
}

function syncProductCaches(queryClient: QueryClient, product: Product) {
  queryClient.setQueryData(productKeys.adminDetail(product.id), product)
  queryClient.setQueryData<Product[]>(productKeys.adminList(product.producerId), (current) =>
    upsertProductInList(current, product),
  )
}

function invalidateProductLists(queryClient: QueryClient, product: Product) {
  void queryClient.invalidateQueries({ queryKey: productKeys.adminList(product.producerId) })
}

function productEndpoint(productId: string) {
  return `/admin/products/${productId}`
}

async function updateProduct(productId: string, input: UpdateProductInput): Promise<Product> {
  const body = serializeUpdateProductInput(UpdateProductInputSchema.parse(input))
  const data = await apiFetch<unknown>(productEndpoint(productId), { method: 'PATCH', body })
  return parseProductResponse(data)
}

async function activateProduct(productId: string): Promise<Product> {
  const data = await apiFetch<unknown>(`${productEndpoint(productId)}/activate`, {
    method: 'POST',
  })
  return parseProductResponse(data)
}

async function archiveProduct(productId: string): Promise<Product> {
  const data = await apiFetch<unknown>(`${productEndpoint(productId)}/archive`, {
    method: 'POST',
  })
  return parseProductResponse(data)
}

async function updateCheckout(
  productId: string,
  checkoutId: string,
  input: Partial<ProductCheckout>,
): Promise<ProductCheckout> {
  const body = UpdateProductCheckoutInputSchema.parse(input)
  const data = await apiFetch<unknown>(`${productEndpoint(productId)}/checkouts/${checkoutId}`, {
    method: 'PATCH',
    body,
  })
  return ProductCheckoutSchema.parse(data)
}

export function useUpdateProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateProductInput) => updateProduct(productId, input),
    onSuccess: (product) => {
      syncProductCaches(queryClient, product)
      invalidateProductLists(queryClient, product)
    },
  })
}

export function useActivateProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => activateProduct(productId),
    onSuccess: (product) => {
      syncProductCaches(queryClient, product)
      invalidateProductLists(queryClient, product)
    },
  })
}

export function useArchiveProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => archiveProduct(productId),
    onSuccess: (product) => {
      syncProductCaches(queryClient, product)
      invalidateProductLists(queryClient, product)
    },
  })
}

export function useUpdateCheckoutMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      checkoutId,
      input,
    }: {
      checkoutId: string
      input: Partial<ProductCheckout> | UpdateProductCheckoutInput
    }) => updateCheckout(productId, checkoutId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.adminDetail(productId) })
      void queryClient.invalidateQueries({ queryKey: productKeys.adminLists() })
    },
  })
}
